import ErrorBoundary from "@/components/common/ErrorBoundary";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useICashTransactionsQuery from "@/hooks/useICashTransactionsQuery";
import { icashTransactionsKeys } from "@/query-keys";
import type { ICashTransactionListItem } from "@/schemas/icashTransactionSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import useColumns from "./_hooks/useColumns";

export const Route = createFileRoute("/_private/employees/$employeeId/icash-transactions/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, params, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const extra = new URLSearchParams({ employeeId: params.employeeId });
    const requestParams = mapTableStateToQueryParams(tableState, extra);
    return queryClient.ensureQueryData({
      queryKey: icashTransactionsKeys.lists(deps.q, extra.toString()),
      queryFn: () => services.getICashTransactions(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const params = Route.useParams();
  const loaderData = Route.useLoaderData();

  const { data: paginatedTransactions } = useICashTransactionsQuery({
    q: search.q!,
    initialData: loaderData,
    params,
  });

  const columns = useColumns();
  const { t } = useTranslation();
  const extra = new URLSearchParams({ employeeId: params.employeeId });

  return (
    <DataTable<ICashTransactionListItem>
      searchPlaceholder={t("kpis.searchPlaceholder")}
      columns={columns}
      data={paginatedTransactions?.items}
      nextCursor={paginatedTransactions?.nextCursor}
      hasMore={paginatedTransactions?.hasMore}
      queryKey={icashTransactionsKeys.lists(search.q, extra.toString())}
      navigateFrom="/employees/$employeeId/icash-transactions"
      searchFrom="/_private/employees/$employeeId/icash-transactions/"
      searchable={true}
      noShadow
    />
  );
}
